<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { VueFinalModal } from 'vue-final-modal';
  // Components
  import { UiModalHeader, ThemeProvider } from '@/shared/ui';
  import {
    MAX_URLS_PER_UPLOAD,
    DUPLICATE_PREFIXES_IN_BATCH_MESSAGE,
    PREFIX_ALREADY_IN_PROGRESS_MESSAGE,
  } from '@/shared/config';
  import {
    DocumentUploadTips,
    DocumentUploadActions,
    DocumentUploadModeSelector,
    DocumentUrlUpload,
    DocumentInfo,
    DocumentDropzone,
    useDocumentUploadModal,
    useUrlUploadModal,
    useFileUploadStore,
    useUrlDataCollector,
    useUrlBatchDuplicatePrefixes,
    usePrefixConflictCheck,
    buildUrlPrefix,
    buildUniquePrefixList,
    urlUploadDataStore,
  } from '@/features/Document';

  const fileUploadStore = useFileUploadStore();
  const { uploadBatchDuplicatePrefixes, dbPrefixConflicts, isCheckingPrefixConflicts } =
    storeToRefs(fileUploadStore);

  defineProps<{
    title: string;
  }>();

  const emit = defineEmits<{
    (e: 'confirm'): void;
    (e: 'close', documentIds?: number[]): void;
    (e: 'upload-success'): void;
  }>();

  const urlDataMap = ref<
    Map<
      number,
      {
        url: string;
        prefixId?: number | string;
        prefixCode?: string;
        numbering: number;
        sequential: number;
        changelog: string;
        managerId?: number | string;
      }
    >
  >(new Map());

  const handleUrlDataUpdate = (
    instanceId: number,
    data: {
      url: string;
      prefixId?: number | string;
      prefixCode?: string;
      numbering: number;
      sequential: number;
      changelog: string;
      managerId?: number | string;
    }
  ) => {
    const next = new Map(urlDataMap.value);
    next.set(instanceId, data);
    urlDataMap.value = next;
  };

  const collector = useUrlDataCollector();

  const modeSelectorKey = ref(0);
  const hasFilesRef = ref(false);
  const cleanStateRef = ref<() => void>(() => {});
  const handleAllFilesDeletedRef = ref<() => void>(() => {});

  const {
    uploadMode,
    urlUploadInstances,
    areAllUrlsValid,
    handleModeChange,
    addUrlUploadInstance,
    removeUrlUploadInstance: removeUrlUploadInstanceOriginal,
    handleUrlValidityChange,
  } = useUrlUploadModal({
    cleanState: () => cleanStateRef.value(),
    handleAllFilesDeleted: () => handleAllFilesDeletedRef.value(),
    isUrlUploadValid: ref(fileUploadStore.isUrlUploadValid),
    hasFiles: hasFilesRef,
    onCancelModeChange: () => {
      modeSelectorKey.value += 1;
    },
  });

  const collectUrlData = () => {
    if (uploadMode.value !== 'url') return [];

    return collector.collectInternalUrlData(
      urlUploadInstances.value,
      (id: number) => urlDataMap.value.get(id) || null
    );
  };

  const removeUrlUploadInstance = (index: number) => {
    const instance = urlUploadInstances.value[index];
    if (instance) {
      const next = new Map(urlDataMap.value);
      next.delete(instance.id);
      urlDataMap.value = next;
    }
    removeUrlUploadInstanceOriginal(index);
  };

  const {
    urlBatchDuplicatePrefixes,
    isInstancePrefixDuplicate,
  } = useUrlBatchDuplicatePrefixes(urlDataMap, urlUploadInstances);

  const urlPrefixesForConflictCheck = computed(() =>
    buildUniquePrefixList(urlUploadInstances.value, (instance) =>
      buildUrlPrefix(urlDataMap.value.get(instance.id))
    )
  );
  const {
    conflictingPrefixes: urlDbPrefixConflicts,
    isCheckingPrefixConflicts: isCheckingUrlPrefixConflicts,
  } = usePrefixConflictCheck(urlPrefixesForConflictCheck);

  const isInstancePrefixDbConflicting = (instanceId: number): boolean => {
    const data = urlDataMap.value.get(instanceId);
    const prefix = buildUrlPrefix(data);
    return prefix !== null && urlDbPrefixConflicts.value.includes(prefix);
  };

  const {
    hasFiles,
    filesLoaded,
    isUploading,
    allChangelogsFilled,
    handleFileUpload,
    handleFileProgress,
    handleFileComplete,
    handleClose,
    handleConfirm,
    handleAllFilesDeleted,
    handleChangelogValidation,
    cleanState,
  } = useDocumentUploadModal(
    emit,
    undefined,
    () => collectUrlData(),
    () => false,
    undefined
  );

  cleanStateRef.value = cleanState;
  handleAllFilesDeletedRef.value = handleAllFilesDeleted;
  watch(
    hasFiles,
    (newVal: boolean) => {
      hasFilesRef.value = newVal;
    },
    { immediate: true }
  );

  onMounted(() => {
    cleanState();
    urlDataMap.value.clear();
    urlUploadDataStore.clearAll();
  });

  defineExpose({
    removeUrlUploadInstance,
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-3/4 divide-y divide-gray-02"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="overflow-auto">
      <UiModalHeader
        :title="title"
        text-color="gray-07"
        class="px-6 py-4 text-sm border-b border-border-color"
        @close="handleClose"
      />
      <div
        id="modal-content"
        class="flex flex-col gap-5 max-h-[80vh] overflow-y-auto"
        :class="filesLoaded ? 'px-3 py-6' : 'px-8 py-6'"
      >
        <DocumentUploadTips class="mb-6" />
        <DocumentUploadModeSelector
          :key="modeSelectorKey"
          :model-value="uploadMode"
          @update:model-value="handleModeChange"
        />
        <DocumentDropzone
          v-if="uploadMode === 'upload'"
          :files-loaded="filesLoaded"
          :multiple="true"
          @files-selected="handleFileUpload"
          @upload-progress="handleFileProgress"
          @upload-complete="
            (_fileId: string, documentIds?: number[]) => handleFileComplete(documentIds)
          "
          @all-files-deleted="handleAllFilesDeleted"
          @all-changelogs-filled="handleChangelogValidation"
        />
        <div
          v-if="uploadMode === 'url'"
          class="flex flex-col gap-4"
        >
          <p
            v-if="urlBatchDuplicatePrefixes.length > 0"
            class="text-red-16 text-sm"
            data-testid="url-duplicate-prefix-message"
          >
            {{ DUPLICATE_PREFIXES_IN_BATCH_MESSAGE }}
          </p>
          <DocumentUrlUpload
            v-for="(instance, index) in urlUploadInstances"
            :key="instance.id"
            :index="index"
            :instance-id="instance.id"
            :is-last="index === urlUploadInstances.length - 1"
            :show-remove-button="urlUploadInstances.length > 1"
            :can-add-more="urlUploadInstances.length < MAX_URLS_PER_UPLOAD"
            :has-batch-duplicate-prefix="isInstancePrefixDuplicate(instance.id)"
            :has-db-prefix-conflict="isInstancePrefixDbConflicting(instance.id)"
            @add-url="addUrlUploadInstance"
            @remove="removeUrlUploadInstance"
            @valid="(isValid: boolean) => handleUrlValidityChange(index, isValid)"
            @data-update="handleUrlDataUpdate(instance.id, $event)"
          />
        </div>
        <p
          v-if="uploadMode === 'upload' && uploadBatchDuplicatePrefixes.length > 0"
          class="text-red-16 text-sm"
        >
          {{ DUPLICATE_PREFIXES_IN_BATCH_MESSAGE }}
        </p>
        <p
          v-if="
            (uploadMode === 'upload' && dbPrefixConflicts.length > 0) ||
              (uploadMode === 'url' && urlDbPrefixConflicts.length > 0)
          "
          class="text-red-16 text-sm"
          :data-testid="
            uploadMode === 'upload' ? 'upload-prefix-conflict-message' : 'url-prefix-conflict-message'
          "
        >
          {{ PREFIX_ALREADY_IN_PROGRESS_MESSAGE }}
        </p>
        <DocumentUploadActions
          :has-files="hasFiles"
          :files-loaded="filesLoaded"
          :is-uploading="isUploading"
          :all-urls-valid="
            uploadMode === 'url'
              ? areAllUrlsValid && urlBatchDuplicatePrefixes.length === 0
              : undefined
          "
          :all-changelogs-filled="uploadMode === 'upload' ? allChangelogsFilled : undefined"
          :has-batch-duplicate-prefixes="
            (uploadMode === 'upload' && uploadBatchDuplicatePrefixes.length > 0) ||
              (uploadMode === 'url' && urlBatchDuplicatePrefixes.length > 0)
          "
          :has-db-prefix-conflict="
            (uploadMode === 'upload' && dbPrefixConflicts.length > 0) ||
              (uploadMode === 'url' && urlDbPrefixConflicts.length > 0)
          "
          :is-checking-prefix-conflicts="
            uploadMode === 'upload' ? isCheckingPrefixConflicts : isCheckingUrlPrefixConflicts
          "
          @close="handleClose"
          @confirm="handleConfirm"
        />
        <DocumentInfo />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
